import { Box, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { useInputSettingsState } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/context/input-settings-context';
import { Recommendation } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/recommendation-assortment.type';
import { BluBox } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/utils/styled-components';

type Props = {
  recommendation: Recommendation;
};

const SkippedCard: React.FC<{ recommendation: Recommendation }> = ({ recommendation }) => {
  return (
    <BluBox>
      <Typography variant="h5" fontWeight={'800'}>
        <FormattedMessage id={`${PAGE_NAME_ID}.skipped.skippedQuestionTitle`} />
      </Typography>

      <Typography variant="body2" fontSize={14} mt={4}>
        <FormattedMessage id={`${PAGE_NAME_ID}.skipped.skippedAnswerContent`} values={{ stock: 60, percentage: 50, br: <br /> }} />
      </Typography>
    </BluBox>
  );
};

const AutoConfirmCard: React.FC<{ recommendation: Recommendation }> = ({ recommendation }) => {
  if (!recommendation) return;

  const formatDaysLeftText = (days: number) => {
    return days === 1 ? <FormattedMessage id={`${PAGE_NAME_ID}.day`} /> : <FormattedMessage id={`${PAGE_NAME_ID}.days`} />;
  };

  const formatDate = (date: string) => {
    return <strong>{dayjs(date).format('D MMMM')}</strong>;
  };

  const formatValues = (recommendation: Recommendation) => {
    return {
      daysLeft: recommendation.autoConfirmInDays,
      daysBeforeDispatch: recommendation.minimumDaysBeforeNewOrderDispatch,
      date: formatDate(recommendation.autoConfirmDate),
      br: <br />,
      daysLeftText: formatDaysLeftText(recommendation.autoConfirmInDays),
      daysBeforeText: formatDaysLeftText(recommendation.minimumDaysBeforeNewOrderDispatch),
    };
  };

  return (
    <BluBox>
      <Typography variant="h5" fontWeight={'800'}>
        <FormattedMessage id={`${PAGE_NAME_ID}.forecast.forecastChangesToOrderTitle`} />
      </Typography>

      <Typography variant="body2" fontSize={14}>
        <FormattedMessage
          id={`${PAGE_NAME_ID}.forecast.forecastChangesToOrderContent.message`}
          values={formatValues(recommendation)}
        />
        <strong>
          <FormattedMessage id={`${PAGE_NAME_ID}.forecast.forecastChangesToOrderContent.bold`} />
        </strong>
      </Typography>
    </BluBox>
  );
};

const ForecastCard: React.FC<{ recommendation: Recommendation }> = ({ recommendation }) => {
  if (!recommendation) return;

  const {
    getOfdSettingsQuery: { data: ofdSettings },
  } = useInputSettingsState();

  const totalQuantity = useMemo(() => {
    return recommendation.products.reduce((prev, curr) => prev + curr.quantity, 0);
  }, [recommendation]);

  return (
    <BluBox>
      <Typography variant="h5" fontWeight={'bold'}>
        <FormattedMessage id={`${PAGE_NAME_ID}.forecast.forecastQuestionTitle`} />
      </Typography>

      <Typography variant="body2" fontSize={14}>
        <FormattedMessage
          id={`${PAGE_NAME_ID}.forecast.forecastAnswerContent`}
          values={{
            stock: (
              <FormattedNumber
                minimumFractionDigits={0}
                maximumFractionDigits={0}
                value={Number(ofdSettings?.inventoryExplanation.startingInventory)}
              />
            ),
            units: <FormattedNumber value={totalQuantity} minimumFractionDigits={0} maximumFractionDigits={0} />,
          }}
        />
      </Typography>
    </BluBox>
  );
};

const PAGE_NAME_ID = 'orderFeed.ofdMySupplyPlan.cards.orderStatusInfo';

const RecommendationCardInfo: React.FC<Props> = ({ recommendation }) => {
  return (
    <Box>
      {recommendation.autoSkipped ? (
        <SkippedCard recommendation={recommendation} />
      ) : (
        <Box display={'flex'} flexDirection={'row'} gap={4} justifyContent={'space-around'}>
          <ForecastCard recommendation={recommendation} />
          <AutoConfirmCard recommendation={recommendation} />
        </Box>
      )}
    </Box>
  );
};

export default React.memo(RecommendationCardInfo);
